import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Grid,
  Typography,
  CircularProgress,
  Autocomplete,
  TextField,
} from "@mui/material";
import _ from "lodash";
import TextButtonComponet from "../../atom/Buttons/TextButton";
import { useQuizFolders } from "../../../hook/useQuizFolders";
import FolderTreeSelection from "../../molecule/FolderTreeSelection";
import QuizeRepository from "../../../core/repository/QuizeRepository";
import { renderTruncatedText } from "../../../core/Constant";

const AdminQuizFolderMapping = ({ onClose, folderId, onSuccess }) => {
  const [quizzes, setQuizzes] = useState([]);
  const [loadingQuizzes, setLoadingQuizzes] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [selectedQuiz, setSelectedQuiz] = useState("");
  const [selectedFolderIds, setSelectedFolderIds] = useState(
    folderId ? [folderId] : []
  );
  const [resetCounter, setResetCounter] = useState(0);
  const [fetchingQuizDetails, setFetchingQuizDetails] = useState(false);

  const { folders, loadingFolders } = useQuizFolders();

  const fetchQuizzes = async (searchValue = "") => {
    setLoadingQuizzes(true);
    try {
      const response = await QuizeRepository.getQuizList({
        name: searchValue,
      });

      const rawQuizzesObj = response?.data?.quizzes || response || {};
      const formattedQuizzes = Object.entries(rawQuizzesObj).map(
        ([id, name]) => ({
          id: String(id),
          name,
          value: String(id),
          label: name,
        })
      );

      setQuizzes(formattedQuizzes);
    } catch (err) {
      console.error("Failed to load quizzes:", err);
    } finally {
      setLoadingQuizzes(false);
    }
  };

  // Initial Fetch
  useEffect(() => {
    fetchQuizzes();
  }, []);

  const debouncedFetch = useMemo(
    () =>
      _.debounce((searchValue) => {
        fetchQuizzes(searchValue);
      }, 300),
    []
  );

  useEffect(() => {
    return () => {
      debouncedFetch.cancel();
    };
  }, [debouncedFetch]);

  const handleInputChange = (event, newInputValue, reason) => {
    if (reason === "input") {
      debouncedFetch(newInputValue);
    }
  };

    // Fetch single quiz details and pre-select mapped folders
    const handleQuizSelect = async (event, newValue) => {
    const quizId = newValue ? String(newValue.id) : "";

    setSelectedQuiz(quizId);
    setErrorMsg("");

    if (!quizId) {
      setSelectedFolderIds(folderId ? [String(folderId)] : []);
      return;
    }

    setFetchingQuizDetails(true);

    try {
      const response = await QuizeRepository.onGetSingleQuizData(quizId);

      const quizResponse = response?.data?.quiz || response?.quiz;
      const quizObj = Array.isArray(quizResponse) ? quizResponse[0] : quizResponse;

      const folders = quizObj?.quiz_directories || quizObj?.directories || [];
      const folderIds = folders
        ?.map((folder) => String(folder.parent_id || folder.id))
        .filter(Boolean);

      const uniqueFolderIds = Array.from(new Set(folderIds));
      setSelectedFolderIds(uniqueFolderIds);
    } catch (err) {
      console.error("Failed to fetch quiz details:", err);

      setSelectedFolderIds(folderId ? [String(folderId)] : []);

      setErrorMsg("Failed to fetch folder mappings for selected quiz.");
    } finally {
      setFetchingQuizDetails(false);
    }
  };

  const handleFolderSelect = useCallback((id) => {
    const formattedId = String(id);
    setSelectedFolderIds((prev) => {
      const prevStringIds = prev.map(String);
      return prevStringIds.includes(formattedId)
        ? prevStringIds.filter((item) => item !== formattedId)
        : [...prevStringIds, formattedId];
    });
    setErrorMsg("");
  }, []);

  const handleCloseAndClear = () => {
    setSelectedQuiz("");
    setSelectedFolderIds([]);
    setErrorMsg("");
    setResetCounter((prev) => prev + 1);
    onClose();
  };

  const onSave = async () => {
    if (!selectedQuiz || selectedFolderIds.length === 0) return;

    setSaving(true);
    setErrorMsg("");

    try {
      const response = await QuizeRepository.mapQuizToFolder({
        quiz_id: selectedQuiz,
        directory_ids: selectedFolderIds.map(Number),
      });

      if (response?.status_code === 1) {
        if (onSuccess) {
          await onSuccess();
        }
        handleCloseAndClear();
      } else {
        setErrorMsg(response?.message || "Failed to map quiz to directory.");
      }
    } catch (err) {
      console.error("Error mapping quiz:", err);
      setErrorMsg(
        err?.response?.data?.message ||
          "Something went wrong while saving mapping."
      );
    } finally {
      setSaving(false);
    }
  };

  const selectedQuizOption = useMemo(
    () => quizzes.find((q) => q.id === selectedQuiz) || null,
    [quizzes, selectedQuiz]
  );

  const isSaveDisabled =
    saving ||
    loadingQuizzes ||
    !selectedQuiz ||
    selectedFolderIds.length === 0 ||
    fetchingQuizDetails;

  return (
    <Box sx={{ p: 2 }}>
      <Grid container spacing={2}>
        {/* Searchable Dropdown Field */}
        <Grid item xs={12}>
          <Typography fontSize={12} sx={{ mb: 0.5, fontWeight: 500 }}>
            Select Quiz *
          </Typography>

          <Autocomplete
            options={quizzes}
            getOptionLabel={(option) => option.label || ""}
            value={selectedQuizOption}
            loading={loadingQuizzes || fetchingQuizDetails}
            onInputChange={handleInputChange}
            onChange={handleQuizSelect}
            // Prevent popper scrolling disconnect issue
            slotProps={{
              popper: {
                disablePortal: false,
              },
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Type to search quiz..."
                size="small"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingQuizzes || fetchingQuizDetails ? (
                        <CircularProgress color="inherit" size={18} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </Grid>

        {/* Folders List Wrapper */}
        <Grid item xs={12}>
          <Typography sx={{ fontSize: 12, color: "#8c8c8c", mb: 1 }}>
            Assign to Folder *
          </Typography>

          <Box key={resetCounter} sx={{ position: "relative" }}>
            {fetchingQuizDetails && (
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: "rgba(255, 255, 255, 0.6)",
                  zIndex: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <CircularProgress size={24} />
              </Box>
            )}

            {/* Use FolderTreeSelection instead of FolderTreeNode */}
            <FolderTreeSelection
              foldersData={folders}
              loading={loadingFolders}
              selectedFolderIds={selectedFolderIds}
              onFolderToggle={handleFolderSelect}
              containerSx={{ maxHeight: 350, overflowY: "auto" }}
            />
          </Box>
        </Grid>

        {/* Error Message Display using renderTruncatedText */}
        {errorMsg && (
          <Grid item xs={12}>
            {renderTruncatedText(errorMsg, {
              limit: 100,
              sx: {
                color: "error.main",
                fontSize: "0.75rem",
                display: "inline-block",
              },
            })}
          </Grid>
        )}

        {/* Form CTA Buttons */}
        <Grid item xs={12}>
          <Box display="flex" justifyContent="flex-end" gap={1} sx={{ mt: 1 }}>
            <TextButtonComponet
              text="Cancel"
              classStyle="btn btn-secondary"
              onButtonClick={handleCloseAndClear}
            />
            <TextButtonComponet
              text={saving ? "Saving..." : "Save Mapping"}
              isDisabled={isSaveDisabled}
              onButtonClick={onSave}
            />
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AdminQuizFolderMapping;